import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, CONTACT, mailto } from "@/data/content";
import { EASE } from "@/components/Reveal";
import { trackMicroConversion } from "@/lib/tracking";

const Navigation = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const onDarkHero = (pathname === "/" || pathname === "/hoerbeispiele" || pathname === "/freie-trauung-krefeld") && !scrolled;
  const isLanding = pathname === "/freie-trauung-krefeld";

  const landingCta = (e) => {
    e.preventDefault();
    trackMicroConversion("cta");
    setOpen(false);
    document.getElementById("anfrage")?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        data-testid="main-navigation"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-creme/85 backdrop-blur-xl border-b border-sand/60"
            : "bg-creme/0 border-b border-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
          <Link to="/" data-testid="nav-logo" className="group flex items-baseline gap-2">
            <span className={`font-serif text-2xl tracking-tight transition-colors duration-500 ${onDarkHero ? "text-creme" : "text-ink"}`}>
              Daviz <span className="font-accent italic text-rust">&</span> Puxi
            </span>
            <span className={`hidden text-[10px] font-semibold uppercase tracking-[0.28em] transition-colors duration-500 sm:block ${onDarkHero ? "text-creme/70" : "text-stone"}`}>
              Freie Trauung Deluxe
            </span>
          </Link>

          <nav className="hidden items-center gap-7 lg:flex" aria-label="Hauptnavigation">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.to.slice(1)}`}
                className={({ isActive }) =>
                  `link-underline text-[13px] font-medium tracking-wide transition-colors duration-500 ${
                    isActive
                      ? "text-rust"
                      : onDarkHero
                        ? "text-creme/85 hover:text-creme"
                        : "text-stone hover:text-ink"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={isLanding ? "#anfrage" : mailto("Anfrage Zeremonie")}
              onClick={isLanding ? landingCta : undefined}
              data-testid="nav-cta-button"
              className="group relative overflow-hidden rounded-full bg-moss px-6 py-2.5 text-[13px] font-semibold text-creme transition-colors duration-300 hover:bg-mossdeep"
            >
              Zeremonie anfragen
            </a>
          </nav>

          <div className="flex items-center gap-3 lg:hidden">
            <a
              href={CONTACT.phoneHref}
              data-testid="nav-phone-mobile"
              onClick={() => trackMicroConversion("phone")}
              aria-label="Anrufen"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-sand bg-creme/70 text-moss backdrop-blur"
            >
              <Phone size={17} />
            </a>
            <button
              onClick={() => setOpen(true)}
              data-testid="nav-menu-open-button"
              aria-label="Menü öffnen"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-sand bg-creme/70 text-ink backdrop-blur"
            >
              <Menu size={19} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[70] flex flex-col bg-mossdeep text-creme"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
          >
            <div className="flex items-center justify-between px-5 py-4">
              <span className="font-serif text-2xl">Daviz <span className="font-accent italic text-sand">&</span> Puxi</span>
              <button
                onClick={() => setOpen(false)}
                data-testid="nav-menu-close-button"
                aria-label="Menü schließen"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-creme/25"
              >
                <X size={20} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-1 px-8" aria-label="Mobile Navigation">
              {[{ to: "/", label: "Home" }, ...NAV_LINKS].map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.05, duration: 0.6, ease: EASE }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                    className={({ isActive }) =>
                      `block border-b border-creme/10 py-3.5 font-serif text-3xl transition-colors ${
                        isActive ? "text-sand italic" : "text-creme"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <div className="px-8 pb-10">
              <a
                href={isLanding ? "#anfrage" : mailto("Anfrage Zeremonie")}
                onClick={isLanding ? landingCta : undefined}
                data-testid="mobile-cta-button"
                className="block rounded-full bg-creme py-4 text-center text-sm font-bold text-mossdeep"
              >
                Zeremonie anfragen
              </a>
              <p className="mt-4 text-center text-xs text-creme/60">{CONTACT.phone} · {CONTACT.email}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
